import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { ChevronRight, Loader2 } from "lucide-react";
import CarCard from "../components/CarCard";
import { useLanguage } from "../contexts/LanguageContext";
import odooApi from "../services/odooApi";

export const BrandPage = () => {
  const { slug } = useParams();
  const { t, tf } = useLanguage();
  const [brand, setBrand] = useState(null);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [brandData, brandCars] = await Promise.all([
          odooApi.getBrand(slug),
          odooApi.getBrandCars(slug),
        ]);
        setBrand(brandData);
        setCars(brandCars);
      } catch (err) {
        console.error("Failed to load brand:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <Loader2 className="w-10 h-10 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t("brandPage.notFound", "Brand Not Found")}</h2>
          <Link to="/cars" className="text-[#D4AF37] hover:underline">{t("carDetail.browseAll", "Browse all cars")}</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`Rent ${brand.name} Cars in Dubai & UAE`}
        description={`Rent ${brand.name} cars in Dubai & UAE. ${brand.carCount}+ vehicles available. Best prices guaranteed. Experience luxury with Velaris Royal.`}
        keywords={`rent ${brand.name.toLowerCase()}, ${brand.name.toLowerCase()} rental dubai, ${brand.name.toLowerCase()} hire uae, luxury car rental`}
        image={brand.logo}
        breadcrumbs={[
          { name: t("nav.home", "Home"), url: "/" },
          { name: t("common.brands", "Brands"), url: "/cars" },
          { name: brand.name },
        ]}
      />

      <div className="min-h-screen bg-white dark:bg-gray-950" data-testid="brand-page">
        <div className="bg-black py-20 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm mb-8">
              <Link to="/" className="text-white/60 hover:text-[#D4AF37]">{t("nav.home", "Home")}</Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <Link to="/cars" className="text-white/60 hover:text-[#D4AF37]">{t("common.brands", "Brands")}</Link>
              <ChevronRight className="w-4 h-4 text-white/40" />
              <span className="text-white">{brand.name}</span>
            </nav>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
              <div className="w-24 h-24 bg-gold-gradient rounded-full flex items-center justify-center">
                <span className="text-black font-bold text-2xl">{brand.logoCode}</span>
              </div>
              <div>
                <h1 className="font-[var(--font-display)] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                  {tf("categoryPage.rent", { name: brand.name }, `Rent ${brand.name}`)}
                </h1>
                <p className="text-white/70 max-w-2xl text-lg">{brand.description || tf("brandPage.descriptionFallback", { brand: brand.name }, `Discover our premium ${brand.name} collection for rent in UAE.`)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-black border-t border-[#D4AF37]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-center gap-8">
              <div className="text-center">
                <p className="text-2xl font-bold text-gold-gradient">{brand.carCount || cars.length}</p>
                <p className="text-white/60 text-sm">{t("common.vehicles", "Vehicles")}</p>
              </div>
              <div className="w-px h-12 bg-[#D4AF37]/30" />
              <div className="text-center">
                <p className="text-2xl font-bold text-gold-gradient">7</p>
                <p className="text-white/60 text-sm">{t("common.cities", "Cities")}</p>
              </div>
              <div className="w-px h-12 bg-[#D4AF37]/30" />
              <div className="text-center">
                <p className="text-2xl font-bold text-gold-gradient">24/7</p>
                <p className="text-white/60 text-sm">{t("common.support", "Support")}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-[var(--font-display)] text-2xl font-bold text-gray-900 dark:text-white">
              {tf("brandPage.availableCars", { brand: brand.name }, `Available ${brand.name} Cars`)}
            </h2>
            <Link to={`/cars?brand=${encodeURIComponent(brand.slug || slug)}`} className="text-[#D4AF37] hover:text-[#C5A028] font-medium flex items-center gap-1">
              {t("btn.viewAll", "View All")}<ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {cars.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="brand-cars-grid">
              {cars.map((car) => (<CarCard key={car.id} car={car} />))}
            </div>
          ) : (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <p className="text-lg">{tf("brandPage.empty", { brand: brand.name }, `No ${brand.name} cars available at the moment. Check back soon!`)}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BrandPage;

